import 'whatwg-fetch';
import 'url-search-params-polyfill';
import 'url-polyfill';
import {
    localData
} from '../lib/LocalData';

const DEFAULTS = {
    ERROR_TEXT: 'There was an error with your request.',
    BASE_API_URL: window.location.origin,
}

/**
 * @class BaseService
 */
export class BaseService {
    constructor() {
            this.options = DEFAULTS;
        }
        /**
         * Takes query object and include array and builds URL
         *
         * @method readResponse
         * @private
         * @param {Object} response
         * @async
         * @return {Promise.<JSON>} response data
         */
    async readResponse(response) {
        return response.json();
    }

    /**
     * Take the body response from a request
     *
     * @method readTextResponse
     * @private
     * @param {Object} response
     * @async
     * @return {Promise.<String>} response data
     */
    async readTextResponse(response) {
        return response.text();
    }

    /**
     * Handle http errors
     *
     * @method handleNetworkErrors
     * @chainable
     * @public
     */
    handleNetworkErrors(response) {
        if (!response.ok) {
            throw Error('Error with network: ' + response.statusText);
        }
        return response;
    }

    /**
     * @method buildUrlAndQuery
     * @private
     * @param {String} url pathname
     * @param {Object} query params
     * @return {String} fullUrl with query string
     */
    buildUrlWithQuery(url, query) {
        if (!query) {
            return url;
        }

        const fullUrl = new URL(url, window.location.origin);
        const params = new URLSearchParams(fullUrl.search);

        Object.keys(query).forEach(key => {
            const value = query[key];

            if (Array.isArray(value) && Number.isInteger(value[0])) {
                value.forEach(item => {
                    params.append(key, item);
                });
            } else {
                params.append(key, query[key]);
            }
        });

        return `${fullUrl}?${params.toString()}`;
    }

    /**
     * @method get
     * @param {String} url pathname
     * @param {Object} query params
     * @return {Promise.<JSON>}
     */
    async get(url, query = {}, method = 'GET') {
        const response = await fetch(
            this.buildUrlWithQuery(this.options.BASE_API_URL + url, query), {
                mode: 'cors',
                method,
                credentials: 'same-origin'
            }
        )        

         if (response.status === 400) {
             return await this.readResponse(response);
         }

         if (response.status >= 200 && response.status < 300) {
             if (response.headers.get('content-type').indexOf('application/json') !== -1) {
                 return await this.readResponse(response);
             }

             const body = await this.readTextResponse(response);

             if (body) {
                return Promise.resolve(body);
             }
         }

         const errorBody = await this.readTextResponse(response);

         if (errorBody) {
             return errorBody;
         }

         return Promise.reject();
    }

    /**
     * @method post
     * @param {String} url pathname
     * @param {Object} param params
     * @return {Promise}
     */
    async post(url, params = {}) {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        
        const response = await fetch(
            this.options.BASE_API_URL + url, {
                method: 'POST',
                headers,
                mode: 'cors',
                body: JSON.stringify(params),
                credentials: 'same-origin'
            }
        );

        if (response.status === 400) {

            return Promise.reject(this.options.ERROR_TEXT);
        }

        if (response.status >= 200 && response.status < 300) {
            if (response.headers.get('content-type') && response.headers.get('content-type').indexOf('application/json') !== -1) {
                return await this.readResponse(response);
            }

            const body = await this.readTextResponse(response);

            if (body) {
                return Promise.resolve(body);
            }

            return Promise.resolve();
        }

        return Promise.reject();
    }

    /**
     * @method PATCH
     * @param {String} url pathname
     * @param {Object} param params
     * @return {Promise}
     */
    async patch(url, params = {}) {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        console.log('(Base service) BASE_API_URL: ' + this.options.BASE_API_URL)
        console.log('(Base service) url: ' + url);
        console.log('(Base Service) params: ', params);

        const response = await fetch(
            this.options.BASE_API_URL + url, {
                method: 'PATCH',  
                headers,
                mode: 'cors',
                body: JSON.stringify(params),
                credentials: 'same-origin'
            }
        );

        if (response.status === 400) {

            return Promise.reject(this.options.ERROR_TEXT);  
        }

        if (response.status >= 200 && response.status < 300) {
            if (response.headers.get('content-type') && response.headers.get('content-type').indexOf('application/json') !== -1) {
                return await this.readResponse(response);
            }

            const body = await this.readTextResponse(response);

            if (body) {
                return Promise.resolve(body);
            }

            return Promise.resolve();
        }

        return Promise.reject();
    }

    /**
     * Get the data from local storage
     *
     * @method getLocal
     * @param {String} name
     * @return {Promise.<JSON>}
     */
    async getLocal(name) {
        return localData.getItem(name);
    }

    /**
     * Save the data to local storage
     *
     * @method setLocal
     * @param {String} name
     * @param {Object} data
     * @return {Promise.<JSON>}
     */
    async setLocal(name, data) {
        localData.setItem(name, data);

        return true;
    }

    /**
     * Delete an item from local storage
     *
     * @method deleteLocal
     * @param {String} name
     * @return {Promise.<JSON>}
     */
    async deleteLocal(name) {
        localData.removeItem(name);

        return true;
    }

}